import { ref } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { z } from "zod";
import { Ok, Err, Result } from "ts-results";

export const elementStyles = {
    base: {
        color: "#000000",
        fontWeight: 400,
        fontFamily:
            'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
        fontSize: "14px",
        fontSmoothing: "antialiased",
        "::placeholder": {
            color: "#78716c",
        },
        ":-webkit-autofill": {
            color: "#78716c",
        },
        ":focus": {},
    },
    invalid: {
        color: "#E25950",
        "::placeholder": {
            color: "#FFCCA5",
        },
    },
};

export const elementClasses = {
    focus: "focused",
    empty: "empty",
    invalid: "invalid",
};

export const billingDetailsSchema = z.object({
    name: z.string().min(2).max(128).trim(),
    country: z.string().min(2),
    postal_code: z.string().min(3).max(10),
});

export type BillingDetails = z.infer<typeof billingDetailsSchema>;

const DEV_KEY =
    "pk_test_51JsOKHJ9tIQH9YIAUPeVLnYYZAb5B6Z8sFXDQpU0NxR1DYRvwqMvpjXG5yEoXAqOrl254F99OoK3gGlJi9dx1cDu00o96vbqi9";

const PROD_KEY =
    "pk_live_51JsOKHJ9tIQH9YIAlLi8C4w4S2lgXIqJKlfN2Im6kFNdzqOJH5ucC8gwlwztYLr8jW9b0pDhXasYX4BXUaaRQJjj009Rgl93Z1";

const STRIPE_KEY = import.meta.env.DEV ? DEV_KEY : PROD_KEY;

export function usePaymentGateway(clientSecret: string) {
    const stripe = ref<stripe.Stripe>();
    const stripeElements = ref<stripe.elements.Elements>();
    const stripeCardNumber = ref<stripe.elements.Element>();
    const stripeCardExpiry = ref<stripe.elements.Element>();
    const stripeCardCvc = ref<stripe.elements.Element>();
    const setupIntent = ref<stripe.setupIntents.SetupIntent>();

    function initStripe() {
        stripe.value = Stripe(STRIPE_KEY);

        stripeElements.value = stripe.value.elements();
        stripeCardNumber.value = stripeElements.value.create("cardNumber", {
            style: elementStyles,
            classes: elementClasses,
        });
        stripeCardExpiry.value = stripeElements.value.create("cardExpiry", {
            style: elementStyles,
            classes: elementClasses,
        });
        stripeCardCvc.value = stripeElements.value.create("cardCvc", {
            style: elementStyles,
            classes: elementClasses,
        });

        stripeCardNumber.value.mount("#card-number");
        stripeCardExpiry.value.mount("#card-expiry");
        stripeCardCvc.value.mount("#card-cvc");
    }

    async function prepareSetupIntent(
        billingDetails: BillingDetails,
    ): Promise<Result<void, Error>> {
        if (stripe.value === undefined) {
            return new Err(new Error("Stripe not initialized"));
        }

        if (stripeCardNumber.value === undefined) {
            return new Err(new Error("Stripe card number not initialized"));
        }

        try {
            const response = await stripe.value.confirmCardSetup(clientSecret, {
                payment_method: {
                    card: stripeCardNumber.value,
                    billing_details: {
                        name: billingDetails.name,
                        address: {
                            country: billingDetails.country,
                            postal_code: billingDetails.postal_code,
                        },
                    },
                },
            });

            if (response.error) {
                // Inform the user if there was an error.
                const errorElement = document.getElementById("card-errors");

                if (errorElement) {
                    errorElement.textContent = String(response.error.message);
                }

                return new Err(new Error(String(response.error)));
            }

            setupIntent.value = response.setupIntent;

            return Ok.EMPTY;
        } catch (err) {
            return new Err(new Error(String(err)));
        }
    }

    async function subscribe(
        plan: string,
        additionalBillingDetails: BillingDetails,
        teamId?: number,
    ) {
        const details = billingDetailsSchema.safeParse(
            additionalBillingDetails,
        );

        if (details.success === false) {
            const er = details.error;
            return new Err(er);
        }

        const res = await prepareSetupIntent(details.data);

        if (!res.ok) {
            return res;
        }

        if (setupIntent.value === undefined) {
            return new Err(new Error("Problem setting up intent"));
        }

        return await new Promise<Result<void, Error>>((resolve, reject) => {
            router.post(
                route("app.subscription.create", { team: teamId }),
                {
                    plan: plan,
                    paymentMethod: setupIntent.value?.payment_method,
                },
                {
                    onSuccess: () => {
                        resolve(Ok.EMPTY);
                    },

                    onError: (error) => {
                        reject(new Err(new Error(error.message)));
                    },
                },
            );
        });
    }

    return {
        init: initStripe,
        subscribe,
    };
}
